import styled, { css } from 'styled-components'
import { useLockFees } from '../fees'
import { fmtUnits } from '../format'
import { priceOf, type PriceState } from '../logic/price'
import { DISPLAY, Skel } from '../ui'

/** The price of a new lock on either locker, read live (the same reads as the Fee step; wagmi shares them). */
export function useLockPrice(): PriceState {
  const v3 = useLockFees('v3')
  const v2 = useLockFees('v2')
  return priceOf(v3, v2)
}

/**
 * FREE, said big (Bez 2026-10-09: "the service being FREE must be bold"). The word is drawn in the gold of the CADINU Lock medal and is
 * shown only when the lockers answer 0 BNB and 0 CBON (logic/price.ts); a non-zero price takes the same place with the price, and while
 * the price is read the space is held by a placeholder of the same size, so nothing moves.
 */
/** solid medal gold, struck: two extruded layers in the medal's darker golds and a soft gold glow (no clipped gradient text) */
const gold = css`
  color: #f7cf62;
  text-shadow: 0 1px 0 #fff3c4, 0 2px 0 #c48a2a, 0 4px 0 #6b4a12, 0 0 28px rgba(255, 207, 90, 0.45);
`
const Row = styled.div<{ $size: 'hero' | 'step' | 'chip' }>`
  display: flex;
  align-items: center;
  gap: ${({ $size }) => ($size === 'chip' ? 10 : 16)}px;
  min-width: 0;
  min-height: ${({ $size }) => ($size === 'hero' ? 64 : $size === 'step' ? 72 : 44)}px;
  /* on a phone the big word stands alone and the two lines go under it, rather than squeezing into four */
  @media (max-width: 479px) {
    ${({ $size }) =>
      $size === 'chip'
        ? ''
        : `flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    > span:last-child {
      border-left: 0;
      padding-left: 0;
    }`}
  }
  ${({ theme }) => theme.mediaQueries.md} {
    min-height: ${({ $size }) => ($size === 'hero' ? 84 : $size === 'step' ? 88 : 44)}px;
  }
`
const Word = styled.span<{ $size: 'hero' | 'step' | 'chip' }>`
  flex: none;
  font-family: ${DISPLAY};
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.01em;
  line-height: 1;
  padding-bottom: 4px;
  ${gold}
  font-size: ${({ $size }) => ($size === 'chip' ? 34 : 64)}px;
  ${({ theme }) => theme.mediaQueries.md} {
    font-size: ${({ $size }) => ($size === 'chip' ? 42 : $size === 'step' ? 76 : 80)}px;
  }
`
const Price = styled.span`
  flex: none;
  font-family: ${DISPLAY};
  font-weight: 900;
  font-size: 30px;
  line-height: 1.1;
  color: #f4f6ff;
  ${({ theme }) => theme.mediaQueries.md} {
    font-size: 40px;
  }
`
const Copy = styled.span<{ $size: 'hero' | 'step' | 'chip' }>`
  display: grid;
  gap: 2px;
  min-width: 0;
  padding-left: ${({ $size }) => ($size === 'chip' ? 10 : 16)}px;
  border-left: 2px solid rgba(242, 199, 92, 0.45);
  b {
    font-family: ${DISPLAY};
    font-weight: 800;
    color: #f4f6ff;
    font-size: ${({ $size }) => ($size === 'chip' ? 15 : 18)}px;
    line-height: ${({ $size }) => ($size === 'chip' ? 20 : 24)}px;
  }
  small {
    font-size: ${({ $size }) => ($size === 'chip' ? 13 : 15)}px;
    line-height: ${({ $size }) => ($size === 'chip' ? 18 : 22)}px;
    color: #c9d2e6;
  }
  ${({ theme }) => theme.mediaQueries.md} {
    b {
      font-size: ${({ $size }) => ($size === 'chip' ? 15 : 22)}px;
      line-height: ${({ $size }) => ($size === 'chip' ? 20 : 28)}px;
    }
  }
`

export function priceLabel(p: PriceState): string | null {
  if (p.kind !== 'priced') return null
  const parts = [
    p.native !== undefined ? `${fmtUnits(p.native, 18, true)} BNB` : null,
    p.cbon !== undefined ? `${fmtUnits(p.cbon, 18, true)} CBON` : null,
  ].filter(Boolean)
  return parts.join(' or ')
}

export default function FreeMark({
  price,
  size = 'hero',
  testid = 'free-mark',
  note,
}: {
  price: PriceState
  size?: 'hero' | 'step' | 'chip'
  testid?: string
  note?: string
}) {
  if (price.kind === 'free') {
    return (
      <Row $size={size} data-testid={testid} data-state="free">
        <Word $size={size} data-testid={`${testid}-word`}>
          Free
        </Word>
        <Copy $size={size}>
          <b>{size === 'chip' ? 'No lock fee' : 'Locking is free. For every project.'}</b>
          <small>{note ?? 'You pay only the network fee.'}</small>
        </Copy>
      </Row>
    )
  }
  if (price.kind === 'priced') {
    return (
      <Row $size={size} data-testid={testid} data-state="priced">
        <Price data-testid={`${testid}-price`}>{priceLabel(price)}</Price>
        <Copy $size={size}>
          <b>Lock fee</b>
          <small>Read live from the lock contract.</small>
        </Copy>
      </Row>
    )
  }
  if (price.kind === 'loading') {
    return (
      <Row $size={size} data-testid={testid} data-state="loading" aria-busy="true">
        <Skel $h={size === 'chip' ? 30 : 56} $w={size === 'chip' ? '150px' : '260px'} $r={14} />
      </Row>
    )
  }
  return (
    <Row $size={size} data-testid={testid} data-state="unknown">
      <Copy $size={size} style={{ borderLeft: 0, paddingLeft: 0 }}>
        <b>Lock fee</b>
        <small>Read from the lock contract when you lock.</small>
      </Copy>
    </Row>
  )
}
